import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Flex, useDisclosure, useMultiStyleConfig} from '@chakra-ui/react'
import {useGigya} from '../../context/gigya'
import GigyaModal from '../modals/gigya-modal'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {setCustomData, addAriaLiveToStatusMessages} from '../../utils/gigya'
import {BRAND_KEY} from '../../brand'
import {useWishList} from '../../hooks/use-wish-list'
import GigyaRegisterController from '../../utils/gigya/registration'
import GigyaBasicFormController from '../../utils/gigya/lite-registration'
import {useIntl} from 'react-intl'
import LoadingSpinner from '../loading-spinner'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'
import {css, Global} from '@emotion/react'

const LoginForm = ({variant = 'default', isSubscription = false}) => {
    const {gigyaObject, gigyaLocale} = useGigya()
    const {addLayer} = useGoogleTagManager()
    const stylesLogin = useMultiStyleConfig('Login', {variant})
    const stylesGlobal = useMultiStyleConfig('GigyaGlobal')
    const intl = useIntl()
    const ssrGigyaMethods = useGigyaSSR()

    const FORGOTTEN_PASSWORD_SETTINGS = {
        screenSet: 'Default-RegistrationLogin',
        startScreen: 'gigya-forgot-password-screen',
        containerID: 'gigya-forgotten-password-container',
        lang: gigyaLocale,
        onAfterScreenLoad: () => {
            addAriaLiveToStatusMessages('gigya-forgotten-password-container')

            const forgotPasswordContainer = document.getElementById(
                'gigya-forgotten-password-container',
            )

            if (forgotPasswordContainer) {
                const emailInput = forgotPasswordContainer.querySelector(
                    'input[data-gigya-name="loginID"][type="text"]',
                )

                if (emailInput) {
                    const wrapper = emailInput.closest('.dlg-gigya-input-text-wrapper')

                    if (wrapper) {
                        const existingLabel = wrapper.querySelector('.gigya-label')
                        if (!existingLabel) {
                            const label = document.createElement('label')
                            label.className = 'gigya-label'
                            label.innerHTML =
                                '<span class="gigya-label-text gigya-required-display">Email*</span>'
                            label.setAttribute('for', emailInput.id || 'loginID')

                            wrapper.insertBefore(label, emailInput)
                        }
                    }
                }
            }
        },
    }

    const {isOpen, onOpen, onClose} = useDisclosure()

    const {data: basket} = useCurrentBasket()
    const {data: wishListData} = useWishList()

    useEffect(() => {
        if (!gigyaObject) return

        window.DLG = {
            ...(window.DLG || {}),
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.LOAD.FORGOTTEN_PASSWORD') {
                        onOpen()
                    }
                    if (e === 'GIGYA.CLOSE.FORGOTTEN_PASSWORD') {
                        onClose()
                    }
                    if (e === 'GIGYA.REDIRECT.LOGIN') {
                        gigyaObject?.accounts?.hideScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                        })
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [gigyaObject])

    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (!gigyaObject) return

        gigyaObject?.accounts.showScreenSet({
            screenSet: 'Default-RegistrationLogin',
            startScreen: 'gigya-login-screen',
            containerID: 'login',
            lang: gigyaLocale,
            deviceType: 'desktop',
            onBeforeScreenLoad: (event) => {
                if (!event.context || event.context != 'ownid-reg-completion') {
                    //For registration completion screen
                    if (
                        event.nextScreen === 'gigya-complete-registration-screen' &&
                        String(event?.response?.errorCode) === '206001'
                    ) {
                        GigyaRegisterController.showCompleteRegistration({
                            gigyaObject,
                            event,
                            gigyaLocale,
                            GigyaRegisterController,
                            GigyaBasicFormController,
                        })

                        return false
                    }
                }

                if (!event.context) {
                    if (
                        event.nextScreen === 'gigya-verification-pending-screen' &&
                        String(event?.response?.errorCode) === '206002'
                    ) {
                        gigyaObject.accounts.showScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                            startScreen: 'gigya-verification-pending-screen',
                            regToken:
                                event?.response?.response?.regToken || event?.response?.regToken,
                            lang: gigyaLocale,
                            onBeforeScreenLoad: () => {
                                const closeBtn = document.querySelector(
                                    '.gigya-screen-dialog-top .gigya-screen-dialog-close a',
                                )

                                const oldImg = closeBtn?.querySelector('img')
                                if (oldImg) {
                                    oldImg.remove()

                                    const svg = document.createElement('span')
                                    svg.innerHTML = `
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                                      <path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  `
                                    closeBtn.appendChild(svg)
                                }

                                const modalTitle = document.querySelector(
                                    '.gigya-screen-dialog-top .gigya-screen-dialog-caption',
                                )

                                if (modalTitle) {
                                    modalTitle.style.display = 'none'

                                    const customTitle = document.createElement('div')
                                    customTitle.className = modalTitle.className
                                    customTitle.id = 'custom-modal-title'

                                    customTitle.textContent = intl.formatMessage({
                                        id: 'screensets.registration_pending.title',
                                        defaultMessage: 'Your account is almost ready',
                                    })

                                    modalTitle.parentNode.insertBefore(customTitle, modalTitle)
                                }
                            },
                        })
                        return false
                    }
                }
            },
            onAfterScreenLoad: () => {
                setIsLoading(false)

                // Trigger reflow to ensure styles apply
                const modalContainer = document.querySelector('.gigya-screen-dialog')
                if (modalContainer) {
                    modalContainer.style.display = 'none'
                    void modalContainer.offsetHeight
                    modalContainer.style.display = ''
                }

                // Apply MCP id to the email input field
                const emailInput = document.querySelector('input[type="text"]')
                if (emailInput) {
                    emailInput.id = 'bx-element-2246463-L0c12Bj-input'
                }

                const headerElement = document.querySelector(
                    '#login .gigya-composite-control.gigya-composite-control-header.dln-loginForm__paragraph',
                )

                if (headerElement) {
                    const label = document.createElement('p')
                    label.className = 'required-fields-label'
                    label.style.marginTop = '16px'
                    label.style.fontSize = '12px'
                    label.style.color = 'var(--chakra-colors-text-secondary)'
                    label.textContent = intl.formatMessage({
                        id: 'login.required_fields_label',
                        defaultMessage: 'Asterisks * indicate required fields',
                    })
                    headerElement.appendChild(label)
                }
            },
            onBeforeValidation: () => {},
            onBeforeSubmit: () => {
                gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
            },
            onSubmit: (e) => setCustomData(e),
            onAfterSubmit: async (res) => {
                const brands = res?.response?.data?.brands || {}
                GigyaRegisterController.onAfterSubmit(res, ssrGigyaMethods, brands)
                // Apply the class to each form with class gigya-login-form
                const forms = document.querySelectorAll('form.gigya-login-form')
                forms.forEach((form) => {
                    form.classList.add('bx-row-submit-default', 'bx-button')
                })
                localStorage.setItem('guestBasket', JSON.stringify(basket))
                localStorage.setItem('guestWishList', JSON.stringify(wishListData))
                if (res?.response && res?.response?.status !== 'FAIL') {
                    let eventLabel = 'login'
                    if (variant === 'productRegistration') {
                        eventLabel =
                            res.form === 'div.gigya-social-login'
                                ? 'user_detail-login-social'
                                : 'user_detail-login-email'
                    }
                    if (BRAND_KEY === 'kenwood' && variant !== 'productRegistration') {
                        eventLabel = res?.response?.operation?.includes('/accounts.socialLogin')
                            ? 'social'
                            : res?.response?.operation?.includes('/accounts.login')
                            ? 'email'
                            : 'login'
                    }
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event:
                                variant === 'productRegistration'
                                    ? 'productRegistration'
                                    : variant === 'preCheckout'
                                    ? 'checkoutAccess'
                                    : 'reservedArea',
                            eventCategory:
                                variant === 'productRegistration'
                                    ? 'afterSales'
                                    : BRAND_KEY === 'kenwood'
                                    ? 'login'
                                    : 'loyalty',
                            eventAction:
                                variant === 'productRegistration'
                                    ? 'productRegistration'
                                    : variant === 'preCheckout'
                                    ? 'checkout'
                                    : 'reservedArea',
                            eventLabel,
                            ...(variant === 'preCheckout' && {
                                method: res?.response?.operation?.includes('/accounts.socialLogin')
                                    ? 'social'
                                    : res?.response?.operation?.includes('/accounts.login')
                                    ? 'email'
                                    : res?.response?.operation?.includes('notifyLogin')
                                    ? 'faceId'
                                    : 'login_method_placeholder',
                            }),
                        }),
                    )
                }
            },
            onError: () => {},
            onFieldChanged: () => {},
        })

        gigyaObject?.accounts.showScreenSet(FORGOTTEN_PASSWORD_SETTINGS)
    }, [gigyaObject])

    return (
        <>
            <Global
                styles={css`
                    ${stylesGlobal}
                `}
            />{' '}
            <Stack
                {...(isSubscription ? stylesLogin.loginStackSub : stylesLogin.loginStack)}
                __css={stylesLogin}
            >
                {isLoading && (
                    <Box position="relative" w="100%" minH="200px">
                        <Flex
                            position="absolute"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            align="center"
                            justify="center"
                        >
                            <LoadingSpinner />
                        </Flex>
                    </Box>
                )}
                <div id="login"></div>
                <GigyaModal onClose={onClose} isOpen={isOpen}>
                    <div id="gigya-forgotten-password-container"></div>
                </GigyaModal>
            </Stack>
        </>
    )
}

LoginForm.propTypes = {
    variant: PropTypes.string,
    isSubscription: PropTypes.bool,
}

export default LoginForm
